import React, { useEffect, useMemo, useState } from "react";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import CardBillingPYInterfaceUX from "react-lib/apps/HISV3/CLM/CardBillingPYInterfaceUX";
import { dateToStringBE, formatUTCtoBEString } from "react-lib/utils/dateUtils";
import { Checkbox, Icon } from "semantic-ui-react";
import formatComma from "react-lib/utils/formatComma";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import { WidthsType, GenerateTableHeader, State } from "./sequence/BillingPYInterface";

// Types
type CardBillingPYInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: any;
  runSequence: any;
  // Sequence
  BillingPYInterfaceSequence: State["BillingPYInterfaceSequence"];
  masterOptions?: Record<string, any>;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
  languageUX?: any;
};

// Const
const CARD_BILLING_PY_INTERFACE = "CardBillingPYInterface";

const CardBillingPYInterface = (props: CardBillingPYInterfaceProps) => {
  const [selectedPY, setSelectedPY] = useState<any[]>([]);
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);

  // useEffect
  useEffect(() => {
    props.runSequence({
      sequence: "BillingPYInterface",
      restart: true,
    });
  }, []);

  useEffect(() => {
    if (
      props.errorMessage?.[CARD_BILLING_PY_INTERFACE] ||
      props.successMessage?.[CARD_BILLING_PY_INTERFACE]
    ) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const columnTableIconCheck = (data: any, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "center" }}>
        {data === true ? (
          <Icon name="check circle" color="green" size="big" />
        ) : data === false ? (
          <Icon name="times circle" color="red" size="big" />
        ) : (
          ""
        )}
      </div>
    );
  };

  const handleSelectAll = (e: any, v: any) => {
    if (v.checked) {
      let newData = (props.BillingPYInterfaceSequence?.billingPYList || []).map(
        (item: any) => item.id
      );

      setSelectedPY(newData);
    } else {
      setSelectedPY([]);
    }
  };

  const handleSearchData = () => {
    props.runSequence({
      sequence: "BillingPYInterface",
      action: "search",
      cardKey: CARD_BILLING_PY_INTERFACE,
      page: 0,
    });

    setSelectedPY([]);
  };

  const handleClearFilter = () => {
    props.runSequence({
      sequence: "BillingPYInterface",
      action: "clear",
    });

    setSelectedPY([]);
  };

  const handleSaveData = (type: string) => {
    props.runSequence({
      sequence: "BillingPYInterface",
      action: "save",
      cardKey: CARD_BILLING_PY_INTERFACE,
      type,
      selectedPY,
    });

    setSelectedPY([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    props.setProp(`BillingPYInterfaceSequence.searchBillingPY.${data.name}`, data?.value);
  };

  const handleChangeDate = (date: any) => {
    props.setProp(`BillingPYInterfaceSequence.searchBillingPY.createDate`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`BillingPYInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "BillingPYInterface",
      action: "search",
      cardKey: CARD_BILLING_PY_INTERFACE,
      [key]: value,
    });

    setSelectedPY([]);
  };

  const handleDisabledAddNEdit = useMemo(() => {
    if (selectedPY.length === 0) return true;
  }, [selectedPY]);

  const billingPYData = useMemo(() => {
    return (props.BillingPYInterfaceSequence?.billingPYList || []).map((item: any) => {
      let createdName = (props.BillingPYInterfaceSequence?.userOptions || [])?.find(
        (data: any) => data?.id === item?.created_by
      );

      return {
        ...item,
        check: columnTableCenter(
          <Checkbox
            checked={selectedPY.includes(item.id)}
            disabled={item.order_ack_by_name}
            onChange={(e: any, v: any) => {
              if (v.checked) {
                setSelectedPY([...selectedPY, item.id]);
              } else {
                let newData = selectedPY.filter((key: any) => key !== item.id);
                setSelectedPY(newData);
              }
            }}
          />,
          { padding: "10px" }
        ),
        ...billingPYcolumns.reduce((data: any, header) => {
          const key = header.accessor;

          if (key === "created_by") {
            data[key] = columnTableCenter(
              (createdName?.full_name_code || "").replace(/\s*\(.*?\)/g, "")
            );
          } else if (key === "created_at") {
            data[key] = columnTableCenter(dateToStringBE(item[key]));
          } else if (["value"].includes(key)) {
            data[key] = columnTableCenter(formatComma(item[key]));
          } else if (["valid", "sent"].includes(key)) {
            data[key] = columnTableIconCheck(item[key]);
          } else {
            data[key] = columnTableCenter(item[key]);
          }

          return data;
        }, {}),
      };
    });
  }, [
    selectedPY,
    props.BillingPYInterfaceSequence?.billingPYList,
    props.BillingPYInterfaceSequence?.userOptions,
    billingPYcolumns,
  ]);

  return (
    <div>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_BILLING_PY_INTERFACE}`, null);
            props.setProp(`successMessage.${CARD_BILLING_PY_INTERFACE}`, null);
          }}
          error={props.errorMessage?.[CARD_BILLING_PY_INTERFACE]}
          success={props.successMessage?.[CARD_BILLING_PY_INTERFACE]}
          languageUX={props.languageUX}
        />
      )}

      <CardBillingPYInterfaceUX
        paginatedItems={props.BillingPYInterfaceSequence?.paginatedItems}
        searchBillingPY={props.BillingPYInterfaceSequence?.searchBillingPY}
        onClickClear={handleClearFilter}
        onChangeData={handleChangeData}
        onChangeDate={handleChangeDate}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        billingPYData={billingPYData}
        billingPYcolumns={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedPY.length ===
                    (props.BillingPYInterfaceSequence?.billingPYList || []).length &&
                  (props.BillingPYInterfaceSequence?.billingPYList || []).length > 0
                }
                onChange={handleSelectAll}
              />
            ),
            accessor: "check",
            width: 60,
          },
          ...billingPYcolumns,
        ]}
        buttonSeach={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSearchData}
            // data
            paramKey={`${CARD_BILLING_PY_INTERFACE}_search`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_BILLING_PY_INTERFACE}_search`]}
            // config
            color="blue"
            size="medium"
            fluid={true}
            title="Search"
          />
        }
        buttonAdd={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={() => handleSaveData("add")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_BILLING_PY_INTERFACE}_add`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_BILLING_PY_INTERFACE}_add`]}
            // config
            color="blue"
            size="medium"
            fluid={true}
            title="Add Record"
          />
        }
        buttonEdit={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={() => handleSaveData("edit")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_BILLING_PY_INTERFACE}_edit`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_BILLING_PY_INTERFACE}_edit`]}
            // config
            color="green"
            size="medium"
            fluid={true}
            title="Edit Date"
          />
        }
      />
    </div>
  );
};

CardBillingPYInterface.displayName = "CardBillingPYInterface";

export default React.memo(CardBillingPYInterface);

const columnWidths: WidthsType = {
  id: 70,
  valid: 70,
  error_message: 150,
  created_by: 120,
  created_at: 150,
  sent: 70,
  file_name: 160,
  version: 80,
  payment: 120,
  record_type: 120,
  receipt_no: 160,
  payment_type: 120,
  bank: 100,
  payment_no: 180,
  value: 120,
};

const billingPYcolumns = GenerateTableHeader(columnWidths);
